import { useCallback } from 'react'
import { useGlobalStore } from '@highlight/ipc/react'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { useToastContext } from './context'
import OverlayIpcRenderer from '@renderer/ipc/OverlayIpc.renderer'
import { appDeepLink } from '@shared/config/Config'
import { ToastOptionsItem } from './options-item'

interface ToastNotificationToggleProps {
  ipcKey: string
  analyticsFeature: string
}

export function ToastNotificationToggle({ ipcKey, analyticsFeature }: ToastNotificationToggleProps) {
  const [notificationsEnabled] = useGlobalStore<boolean>('settings', ipcKey, true)
  const toastContext = useToastContext()

  const onMuteFor1Hour = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()
      const oneHour = 1

      await OverlayIpcRenderer.muteNotificationForHours({ ipcKey, hours: oneHour })
      toastContext?.close()

      AmplitudeRenderer.trackEvent('Notification Setting', {
        feature: analyticsFeature,
        source: 'Toast',
        action: 'Muted 1 Hour',
      })
    },
    [ipcKey, analyticsFeature, toastContext],
  )

  const onMuteUntilMorning = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()
      const hourOfDay = new Date().getHours()
      const hoursUntilFourAM = (24 - hourOfDay + 4) % 24

      await OverlayIpcRenderer.muteNotificationForHours({ ipcKey, hours: hoursUntilFourAM })
      toastContext?.close()

      AmplitudeRenderer.trackEvent('Notification Setting', {
        feature: analyticsFeature,
        source: 'Toast',
        action: 'Muted until Morning',
      })
    },
    [ipcKey, analyticsFeature, toastContext],
  )

  const onMoreOptions = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()

      const urlWithHighlight = `${appDeepLink('settings/notifications')}?highlight=${encodeURIComponent(ipcKey)}`
      window.open(urlWithHighlight, '_blank')

      AmplitudeRenderer.trackEvent('Notification Setting', {
        feature: analyticsFeature,
        source: 'Toast',
        action: 'Opened More Options',
      })
    },
    [analyticsFeature, ipcKey],
  )

  const handleEnable = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()

      await OverlayIpcRenderer.unmuteNotification(ipcKey)

      AmplitudeRenderer.trackEvent('Notification Setting', {
        feature: analyticsFeature,
        source: 'Toast',
        action: 'Enabled',
      })
    },
    [ipcKey, analyticsFeature],
  )

  if (!notificationsEnabled) {
    return <ToastOptionsItem onClick={handleEnable}>Unmute notification</ToastOptionsItem>
  }

  return (
    <>
      <ToastOptionsItem onClick={onMuteFor1Hour}>Mute for 1 hour</ToastOptionsItem>
      <ToastOptionsItem onClick={onMuteUntilMorning}>Mute until tomorrow</ToastOptionsItem>
      <ToastOptionsItem onClick={onMoreOptions}>More options...</ToastOptionsItem>
    </>
  )
}
